import { useState } from 'react'
import EventLayout from '@/components/layouts/EventLayout'
import TabbedPage from '@/components/shared/TabbedPage'
import { TabIconLabel } from '@/components/shared/TabIconLabel'
import { CalendarOutlined, UserOutlined, EnvironmentOutlined } from '@ant-design/icons'
import TracksTab from './components/TracksTab'
import SpeakersTab from './components/SpeakersTab'
import VenuesTab from './components/VenuesTab'
import BuilderTab from './components/BuilderTab'

interface Event {
  id: number
  name: string
  slug: string
  start_date: string
  end_date: string
  speaker_role_order: ('speaker' | 'moderator' | 'chairperson')[]
}

interface Track {
  id: number
  name: string
  color: string
  position: number
}

interface AgendaDay {
  id: number
  scheduled_on: string
  position: number
  tracks: Track[]
}

interface Speaker {
  id: number
  full_name: string
  email: string | null
  job_title: string | null
  organization: string | null
  phone_number: string | null
  short_bio: string | null
  visible: boolean
  photo_url: string | null
  role: 'speaker' | 'moderator' | 'chairperson'
  position: number
}

interface Venue {
  id: number
  title: string
}

interface SessionSpeaker {
  id: number
  full_name: string
}

interface Session {
  id: number
  title: string
  description: string | null
  session_date: string
  start_time: string | null
  end_time: string | null
  visible: boolean
  agenda_day_id: number
  agenda_track_id: number
  agenda_venue_id: number | null
  venue_name: string | null
  track_name: string | null
  track_color: string | null
  position: number
  speakers: SessionSpeaker[]
}

interface IndexProps {
  event: Event
  agenda_days: AgendaDay[]
  speakers: Speaker[]
  venues: Venue[]
  sessions: Session[]
}

export default function Index({ event, agenda_days, speakers, venues, sessions }: IndexProps) {
  // When set, the Tracks tab swaps its list for the in-place Builder scoped to the chosen day.
  // `null` shows the Tracks list.
  const [builderView, setBuilderView] = useState<{ dayId: number } | null>(null)

  const tabs = [
    {
      key: 'tracks',
      label: <TabIconLabel icon={<CalendarOutlined />} label="Tracks" />,
      children: builderView ? (
        <BuilderTab
          event={event}
          agendaDays={agenda_days}
          sessions={sessions}
          speakers={speakers}
          venues={venues}
          initialDayId={builderView.dayId}
          onBack={() => setBuilderView(null)}
        />
      ) : (
        <TracksTab
          event={event}
          agendaDays={agenda_days}
          onOpenBuilder={(dayId) => setBuilderView({ dayId })}
        />
      )
    },
    {
      key: 'speakers',
      label: <TabIconLabel icon={<UserOutlined />} label="People" />,
      children: <SpeakersTab event={event} speakers={speakers} speakerRoleOrder={event.speaker_role_order} />
    },
    {
      key: 'venue',
      label: <TabIconLabel icon={<EnvironmentOutlined />} label="Venue" />,
      children: <VenuesTab event={event} venues={venues} />
    }
  ]

  return (
    <EventLayout>
      <TabbedPage
        title="Agenda Builder"
        subtitle="Build your agenda by creating tracks, speakers and venue."
        tabs={tabs}
        defaultActiveKey="tracks"
        tabSize="small"
        showTabContainer={false}
        containerClassName="px-8 py-6"
      />
    </EventLayout>
  )
}
